import React, { useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';

import {
  ItemForm,
  FormInput,
  FormSwitch,
} from '../../components';
import yup from '../../utility/yup';
import { is, roles } from '../../utility/guard';
import { User } from '../../types/items';
import { Loadable } from '../../store/types';
import { useTitle } from '../../hooks';

const userSchema = yup.object().shape({
  first_name: yup.string()
    .matches(/^[\p{Alpha}\s+(.\-`'&)]+$/u, 'First Name has a wrong symbols')
    .required('Last Name is a required field')
    .trim(),
  last_name: yup.string()
    .matches(/^[\p{Alpha}\s+(.\-`'&)]+$/u, 'Last Name has a wrong symbols')
    .required('Last Name is a required field')
    .trim(),
  city: yup.string(),
  state: yup.string(),
  zip_code: yup.string(),
  is_admin: yup.bool(),
  title: yup.string().max(50),
  license: yup.string().max(50),
});

type Props = {
  creatingMode?: boolean;
  currentMode?: boolean;
  record: Loadable<User>;
  onSubmit: (record: User) => void;
  preventCancelChanges?: boolean;
  backToListLink?: boolean;
  title?: string;
  showOnlyAdminSettings?: boolean;
  showPrivateFields?: boolean;
};

const UserForm = ({
  creatingMode = false,
  record: { loading, data },
  onSubmit,
  preventCancelChanges = false,
  showOnlyAdminSettings = false,
}: Props) => {
  useTitle(data?.ref.full_name_first_last);
  const dispatch = useDispatch();
  const canEdit = data?.permissions.can_edit || false;

  useEffect(() => {
    const hotKeyList = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key?.toLocaleLowerCase() === 'l') {
        event.preventDefault();
        dispatch(push('/dashboard/admin/users'));
      }
    };
    window.addEventListener('keydown', hotKeyList);

    return () => {
      window.removeEventListener('keydown', hotKeyList);
    };
  }, [dispatch]);

  return (
    <ItemForm
      creatingMode={creatingMode}
      initialValues={data}
      validationSchema={userSchema}
      onSubmit={onSubmit}
      loading={loading}
      editPermitted={Boolean(data?.permissions.can_edit)}
      enableReinitialize
      preventCancelChanges={preventCancelChanges}
      validateOnMount={false}
      validateOnBlur={false}
      validateOnChange
    >
      <Grid container spacing={4} hidden={showOnlyAdminSettings}>
        {
          !showOnlyAdminSettings
            ? (
              <>
                <Grid item sm={6} xs={12}>
                  <FormInput
                    label="First Name"
                    name="first_name"
                    disabled={!canEdit}
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormInput
                    label="Last Name"
                    name="last_name"
                    disabled={!canEdit}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInput
                    label="Email"
                    name="email"
                    disabled={!creatingMode}
                  />
                </Grid>
                {!creatingMode
                  && (
                    <>
                      {data?.ref.organization?.common_name
                      && (
                        <Grid item xs={12}>
                          <FormInput
                            label="Organization"
                            name="ref.organization.common_name"
                            disabled
                            spellCheck
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <FormInput
                          label="Business Title"
                          name="title"
                          tooltip="This is the title that will show up for your videos"
                          disabled={!canEdit}
                          spellCheck
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput
                          label="License Number"
                          name="license"
                          tooltip="This is the license number that will show up for your videos. e.g. DRE#12345678"
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput
                          label="Editor Notes"
                          name="editor_notes"
                          type="textarea"
                          tooltip="These notes will be visible to video editors. You can write anything you want here"
                          disabled={!canEdit}
                          spellCheck
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput
                          label="Phone"
                          name="phone"
                          tooltip="Primary phone number, to be used for contact information on videos"
                        />
                      </Grid>
                      <Grid item sm={4} xs={12}>
                        <FormInput
                          label="City"
                          name="city"
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item sm={4} xs={12}>
                        <FormInput
                          label="State"
                          name="state"
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item sm={4} xs={12}>
                        <FormInput
                          label="Zip code"
                          name="zip_code"
                          disabled={!canEdit}
                        />
                      </Grid>
                    </>
                  )}
              </>
            )
            : (
              <>
                <Grid item xs={6}>
                  <FormSwitch
                    label="Active"
                    name="is_active"
                    disabled={!is(roles.admin)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormSwitch
                    label="Super Admin"
                    name="is_super_admin"
                    disabled={!is(roles.superAdmin)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormSwitch
                    label="Email verified"
                    name="email_address_verified"
                    disabled
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormSwitch
                    label="Has attempted attach to organization"
                    name="has_attempted_attach_to_organization"
                    disabled
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInput
                    label="Username"
                    name="username"
                    disabled
                  />
                </Grid>
              </>
            )
        }
      </Grid>
    </ItemForm>
  );
};

export default UserForm;
